import CorbanApi from '@/api/correspondent/correspondent-api';
import { copyText } from '@/utils/clipboard';
import { ROUTE_NAMES } from '@/constants/route-constants';
import { PROFILES } from '../../../common/constants/generic/types';
import i18n from '@/plugins/i18n';
import { withLocaleQuery } from '@/utils/locale-propagation';

/**
 * Monta o link fixo de cadastro (/cadastro/:code) a partir do codigo do corban.
 * @param {VueRouter} router
 * @param {string} code
 * @returns {string}
 */
export function buildCorbanSignupLink(router, code) {
  if (!code) return '';

  const { href } = router.resolve({ name: ROUTE_NAMES.CORBAN_PUBLIC_SIGNUP, params: { code } });

  return withLocaleQuery(`${window.location.origin}${href}`);
}

/**
 * Link da empresa (/cadastro, sem codigo): o cliente nasce sem corban.
 * @param {VueRouter} router
 * @returns {string}
 */
export function buildCompanySignupLink(router) {
  const { href } = router.resolve({ name: ROUTE_NAMES.CORBAN_PUBLIC_SIGNUP });

  return withLocaleQuery(`${window.location.origin}${href}`);
}

/**
 * Link fixo de cadastro de quem esta logado: o do corban, para o corban; o da
 * empresa, para o admin. Carrega ao montar e copia no clique.
 * Requer `appData` injetado e o mixin-message (sendMessage) no componente.
 */
export default {
  data() {
    return {
      signupLink: '',
      copyingSignupLink: false
    };
  },
  computed: {
    isCorbanUser() {
      return String(this.appData?.currentUser?.profileId) === String(PROFILES.CORBAN.id);
    },
    isAdministrativeUser() {
      return String(this.appData?.currentUser?.profileId) === String(PROFILES.ADMINISTRATIVE.id);
    },
    canShareSignupLink() {
      return this.isCorbanUser || this.isAdministrativeUser;
    }
  },
  mounted() {
    if (this.canShareSignupLink) this.loadSignupLink();
  },
  methods: {
    async loadSignupLink() {
      if (this.isAdministrativeUser) {
        this.signupLink = buildCompanySignupLink(this.$router);

        return;
      }

      try {
        const corbanApi = new CorbanApi(this.appData.currentUser);
        const { data } = await corbanApi.findId(this.appData.currentUser.correspondentId);

        this.signupLink = buildCorbanSignupLink(this.$router, data?.publicSignupCode);
      } catch (error) {
        this.signupLink = '';
      }
    },

    /** Copia dentro do clique: o fallback de copia do navegador exige o gesto do usuario. */
    async copySignupLink() {
      if (!this.signupLink) {
        this.copyingSignupLink = true;
        await this.loadSignupLink();
        this.copyingSignupLink = false;
      }

      await this.copyCorbanSignupLink(this.signupLink);
    },

    async copyCorbanSignupLink(link) {
      if (!link) {
        this.sendMessage(i18n.t('customers.corbanSignupLinkUi.linkUnavailable'), 'error');

        return;
      }

      if (await copyText(link)) {
        this.sendMessage(i18n.t('customers.corbanSignupLinkUi.linkCopied'), 'success');

        return;
      }

      this.sendMessage(i18n.t('customers.corbanSignupLinkUi.copyFailed', { link }), 'error');
    }
  }
};
